import React, { useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert, Box, LinearProgress, Stack, Tab, Tabs, Typography } from '@mui/material'
import API from '../../../services/API'
import { UserContext } from '../../../App'
import { TabLabel } from '../../fleetEquipment/vehicles/VehicleComponents/SectionKit'
import DopsList from './DopsList'
import Logbook from './Logbook'
import { apiError } from './dopsShared'

const TABS = ['requests', 'mine', 'logbook']

// /dops - the clinician's DOPS inbox (as assessor), their own DOPS (as
// trainee) and their procedure logbook. ?tab= keeps the tab on reload.
const DopsHome = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useContext(UserContext)
  // Only Governance can open submitted records from the logbook
  const isGovernance =
    !!user?.employee?.teams?.includes('Governance') || !!user?.security?.isSiteAdmin
  const tabParam = searchParams.get('tab')
  const [data, setData] = useState({ asTrainee: [], asAssessor: [] })
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState('')

  useEffect(() => {
    API.get('/dops/mine')
      .then((res) => {
        if (res.status === 200) {
          setData({
            asTrainee: res.data?.asTrainee ?? [],
            asAssessor: res.data?.asAssessor ?? [],
          })
        } else setFetchError(apiError(res, 'Unable to load your DOPS'))
      })
      .catch(() => setFetchError('Unable to load your DOPS'))
      .finally(() => setLoading(false))
  }, [])

  const pending = data.asAssessor.filter((d) => d.status === 'Requested')
  const done = data.asAssessor.filter((d) => d.status !== 'Requested')
  const tab = TABS.includes(tabParam)
    ? tabParam
    : !loading && !pending.length
      ? 'mine'
      : 'requests'

  return (
    <Stack gap={1.5} sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
      <Box>
        <Typography variant="title2" component="div">
          DOPS &amp; Logbook
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          Direct Observation of Procedural Skills and your procedure logbook
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tab}
          onChange={(e, value) => setSearchParams({ tab: value }, { replace: true })}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab
            value="requests"
            label={
              <TabLabel
                label="Requested of me"
                count={pending.length || undefined}
                color="warning"
              />
            }
          />
          <Tab value="mine" label="My DOPS" />
          <Tab value="logbook" label="My logbook" />
        </Tabs>
      </Box>

      {tab !== 'logbook' && loading && <LinearProgress />}
      {tab !== 'logbook' && fetchError && <Alert severity="error">{fetchError}</Alert>}

      {tab === 'requests' && !loading && !fetchError && (
        <Stack gap={2}>
          <Box>
            <Typography fontWeight={800} mb={1}>
              Waiting for you
            </Typography>
            <DopsList
              items={pending}
              role="assessor"
              empty="No DOPS are waiting for you. When a colleague asks you to assess a procedure it will appear here."
            />
          </Box>
          {done.length > 0 && (
            <Box>
              <Typography fontWeight={800} mb={1}>
                Previously assessed
              </Typography>
              <DopsList items={done} role="assessor" empty="" />
            </Box>
          )}
        </Stack>
      )}

      {tab === 'mine' && !loading && !fetchError && (
        <DopsList
          items={data.asTrainee}
          role="trainee"
          empty="You haven't requested any DOPS yet. Open a completed intervention on an ePCR and choose Request DOPS."
        />
      )}

      {tab === 'logbook' && <Logbook endpoint="/logbook/me" canOpenRecords={isGovernance} />}
    </Stack>
  )
}

export default DopsHome
